import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Empty, Table, TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

// services
import NotificationService from 'core/services/notification';
import { onDeleteBank, onGetBanks } from 'modules/banks/services/banks';

// models
import { Bank } from 'modules/banks/models/banks';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';
import { isRTL } from './../../../../core/services/language';

function ListBanksPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [banks, setBanks] = useState<Bank[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedBank, setTempDeletedBank] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Bank>['columns'] = [
		{
			title: t('BANKS.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <p className="mb-0 fw-medium">{isRTL() ? name.ar : name.en}</p>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => (
				<span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{t(active ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE')}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, bank) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="bank_update">
						<Link to={`${bank.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="bank_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(bank.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getBanks();
	}, []);

	const getBanks = async () => {
		try {
			setIsLoading(true);

			const response = await onGetBanks();
			setBanks(response.data.data.banks);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetBanks(page);
			setBanks(response.data.data.banks);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onShowConfirmation = (bankId: number) => {
		setShowConfirmation(true);
		setTempDeletedBank(bankId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteBank(tempDeletedBank as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('BANKS.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getBanks();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.ERROR'), t('BANKS.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('BANKS.MANAGEMENT_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer listData">
					<header>
						<div>
							<h5>
								{totalRecords} {t('BANKS.MANAGEMENT_COUNT')}
							</h5>
							<p>{t('BANKS.MANAGEMENT_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="bank_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>
					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={banks}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('BANKS.DELETE_TITLE')}
				description={t('BANKS.DELETE_DESCRIPTION')}
				note={t('BANKS.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListBanksPage;
